<!--
	T34: THE MARKUP OF THIS PANEL ONLY. Nothing below the markup was touched -- not the poll, not the
	ten-a-page, not the three silent failure paths, not one method of the `<script>` block. The panel
	is `r13-top-bar.html:233-266`: a `.ftd-r13-pop` of a `__head`, a `__body` of `.ftd-r13-job` rows
	and a `__foot` holding Load More.. as a `.ftd-btn--block`.

	T11 HAD LANDED BEFORE THIS CHANGE and it wins on substance, so what is drawn here is T11's row and
	T11's count, not today's: the file's name as the title, the second line reading kind, object,
	method and status, the red count taken from the paginator's `total`, and the "not started" state.
	Every one of those expressions is T11's, copied across unaltered; only the elements holding them
	and their classes are T34's.

	THE STATUS DISC takes r13's four modifiers, and it takes them FROM `statusColor()` -- the method
	T11 left, which this task must not edit -- so Log #289's "not started" still lands on the neutral
	`--info` disc and not on the amber one.

	THE PROCESSING ICON SPINS (Log #374 point 26). The motion is the foundation's own `ftd-spin`
	keyframes, applied in `_topbar.scss` to `.ftd-r13-job__icon--work > i`; no second animation is
	written anywhere.

	THE DISMISS IS AN ICON (`bx-x`), not the literal character `✕`, which is what this file carried.

	NOT BUILT, and that is Tristian's decision and not an oversight (Log #374 point 26): the empty
	state with Import and Export pills and the fetching line "Loading File Jobs...". Today's
	"No File Jobs" wording stands as it is.
-->
<template>
	<b-dropdown
		class="ftd-menuwrap ftd-r13-bellwrap"
		toggle-class="ftd-r13-ctl ftd-r13-ctl--icon ftd-r13-bell"
		right
		menu-class="ftd-r13-pop"
		variant="black"
		no-caret
		title="File Jobs">
		<template v-slot:button-content>
			<i class="bx bx-task" aria-hidden="true"></i><span class="ftd-sr">File Jobs</span>
			<!--
				T11, Log #288: THE NUMBER WAITING, NOT THE NUMBER LOADED. This printed
				`notifications.length`, which is the page size: checked on the site it read 10 while
				17 were waiting. `total` is the paginator's own, which the response has always
				carried and `fetchNotifications()` has always read.
				T34 draws it as r13's `.ftd-r13-bell__n` and leaves the expression alone. The bell
				with NO count is today's state and is kept (task point 10).
			-->
			<span v-if="total" class="ftd-r13-bell__n">{{ total }}</span>
		</template>

		<div class="ftd-r13-pop__head">
			<span class="ftd-r13-pop__title">File Jobs</span>
			<a href="javascript:void(0)" class="ftd-link" @click="clearAllNotifications">Clear All</a>
		</div>

		<!--
			The simplebar is today's and the list still has ONE scroller, not the panel's and its own.

			FIX ROUND 1: ITS INLINE `max-height: 530px` AND `overflow-y: auto` ARE GONE, and both now
			come from `.ftd-r13-pop__body` in `_topbar.scss`. Vue puts a class and a style attribute on
			the SAME root node, so with the class added and the inline height left in place the two
			numbers fought and the inline one won: the panel went on scrolling at 530px while the rule
			shipped 400. THE MOCK'S 400px GOVERNS (`r13-top-bar.css:188`, and ledger T34-159 states the
			same reading), so the height is the stylesheet's and the rule is not dead weight. The
			530px was not T11's -- it is today's, which T11 left alone -- and nothing of T11's substance
			is touched here: the title, the detail line, the count at the toggle and the "not started"
			state are all still T11's. `min-height: 100px` stays inline, exactly as today writes it.
		-->
		<simplebar v-if="notifications.length" class="ftd-r13-pop__body" data-simplebar style="min-height: 100px;">
			<a v-for="n in notifications" :key="n.id"
				:href="notifiableUrl(n)"
				:target="n.notifiable_type.includes('ExportFile') ? '_blank' : ''"
				class="ftd-r13-job">

				<span class="ftd-r13-job__icon" :class="{
						'ftd-r13-job__icon--done': statusColor(n) === 'bg-success',
						'ftd-r13-job__icon--work': statusColor(n) === 'bg-warning',
						'ftd-r13-job__icon--bad': statusColor(n) === 'bg-danger',
						'ftd-r13-job__icon--info': statusColor(n) === 'bg-info'
					}">
					<i :class="statusIcon(n)"></i>
				</span>
				<!--
					T11: `min-width: 0` is what MAKES the title's ellipsis below draw. The flex item
					holding the name cannot otherwise shrink below its min-content width -- and
					`white-space: nowrap` makes that the WHOLE file name. Without it the name pushes
					the panel open instead of clipping: measured in Chrome over the branch's own
					app.css, a 76-character name gave a 559px media-body inside a 320px menu, an
					avatar squashed from 32px to 11.6px, a Dismiss button outside the panel and no
					ellipsis at all (clientWidth === scrollWidth).
					T11 asked T34 to keep it, and T34 keeps it: `.ftd-r13-job__text` carries the same
					`min-width: 0` in the sheet's own placement, and the inline declaration stays here
					as well so the behaviour cannot be lost with a class.
					T11's `text-truncate` was a Bootstrap class inside `.ftd`; its three declarations
					are `.ftd-r13-job__title` in `_topbar.scss` now and the truncation is unchanged.
				-->
				<span class="ftd-r13-job__text" style="min-width: 0;">
					<!--
						T11, Log #288: THE ROW IS TITLED WITH THE FILE'S NAME, one line with
						an ellipsis and the full name on hover. `rowTitle()` falls back to
						the title this row printed before this change - "IMPORT – done" -
						whenever the file has been deleted, so such a row reads as it reads
						today rather than losing its title.
					-->
					<span class="ftd-r13-job__title" :title="n.file_name || null">{{ rowTitle(n) }}</span>
					<!--
						"Import · Assets · Asset Tracker · done": kind, object, method
						where there is one, status. Drawn only where the file is known,
						for the same reason as the title above.
					-->
					<span v-if="n.file_name" class="ftd-r13-job__line">{{ rowDetails(n) }}</span>
					<span class="ftd-r13-job__line">
						<span><b>{{ n.successful_items }}/{{ n.total_items }}</b> successful</span>
						<span v-if="n.failed_items" class="ftd-r13-job__fail">({{ n.failed_items }} failed)</span>
						<a v-if="n.file_url" class="ftd-link" :href="n.file_url" target="_blank">Download</a>
						<span class="ftd-r13-job__when">
							<i class="bx bx-time-five"></i>{{ n.created_at | fromNow }}
						</span>
					</span>
				</span>
				<b-button variant="link" size="sm" class="ftd-iconbtn ftd-iconbtn--sm ftd-r13-job__x" @click.stop="dismiss(n.id)"
					title="Dismiss">
					<i class="bx bx-x" aria-hidden="true"></i><span class="ftd-sr">Dismiss</span>
				</b-button>
			</a>
		</simplebar>

		<div v-else class="ftd-r13-pop__empty">
			No File Jobs
		</div>

		<div class="ftd-r13-pop__foot">
			<a v-if="!finished" class="ftd-btn ftd-btn--block" href="javascript:void(0)"
				@click="fetchNotifications">
				<i class="bx bx-down-arrow-circle"></i> Load More..
			</a>
		</div>
	</b-dropdown>
</template>

<script>

import simplebar from "simplebar-vue";

export default {
	name: 'FileNotificationsDropdown',
	components: {
		simplebar
	},
	props:['item_type'],
	data() {
		return {
			notifications: [],
			// T11, Log #288: the paginator's total, which the red count prints. It is the number of
			// jobs waiting; `notifications.length` is only how many of them have been loaded.
			total: 0,
			page: 1,
			perPage: 10,
			loading: false,
			finished: false,
			polling: null
		};
	},
	mounted() {
		this.fetchNotifications();
		this.startPolling();
	},
	beforeDestroy() {
		this.stopPolling();
	},
	methods: {
		/**
		 * T11, Log #288: the row's title is the file's name.
		 *
		 * The fallback is the exact title this row printed before this change. It is reached when the
		 * file has been deleted -- a soft-deleted import and a deleted export alike resolve to no
		 * file at all on the server -- and the walk's rule for that row is that it reads as it reads
		 * today: no blank title, no crash, today's wording.
		 */
		rowTitle(notification) {
			if (notification.file_name) {
				return notification.file_name;
			}

			return notification.type.toUpperCase() + ' – ' + notification.status;
		},

		/**
		 * The second line: kind, object, method where there is one, and status, separated by the
		 * middle dot -- "Import · Assets · Asset Tracker · done".
		 *
		 * Every part but the kind and the status is server-side and may be absent: an export with no
		 * template has no object, an import on a module whose `uses_import_methods` is 0 is sent no
		 * method, and a `method` the server does not recognise prints nothing rather than its code.
		 * `status_label` is the status as the server words it: "not started" for an import that was
		 * uploaded and never run (Log #289), the notification's own status otherwise.
		 *
		 * T14 PART E: `department_name` is the fourth part, between the method and the status -- the
		 * department's name, or "All departments" for a cross-department file, in the Files list's own
		 * wording. Like the two parts before it, it is server-side and absent where there is nothing to
		 * say: a module not held to departments sends none, and so does a row whose file has been
		 * deleted, and the line then reads exactly as it read before.
		 */
		rowDetails(notification) {
			const kind = notification.type
				? notification.type.charAt(0).toUpperCase() + notification.type.slice(1)
				: '';

			return [
				kind,
				notification.object_name,
				notification.method_name,
				notification.department_name,
				notification.status_label || notification.status
			].filter(function (part) {
				return !!part;
			}).join(' · ');
		},

		notifiableUrl(notification) {
			if (!notification || !notification.notifiable_type || !notification.notifiable_id) {
				return 'javascript:void(0)';
			}

			const type = notification.notifiable_type;
			const id = notification.notifiable_id;

			if (type.includes('ExportFile')) {
				return `/${this.item_type}/files/${id}/open`;
			} else if (type.includes('ImportFile')) {
				return `/${this.item_type}/file_imports/${id}/view`;
			}
			return 'javascript:void(0)';
		},
		async clearAllNotifications() {
			try {
				await axios.post('/api/assetflow/notifications/files/clear-all');
				this.notifications = [];
				this.total = 0; // and the count with them, or the badge outlives the list
				this.finished = true; // no more notifications to load
			} catch (error) {
				console.error('Failed to clear notifications:', error);
			}
		},
		fetchNotifications() {
			if (this.loading || this.finished) return;

			this.loading = true;
			axios
				.get('/api/assetflow/notifications/files', {
					params: {
						page: this.page,
						per_page: this.perPage,
					},
				})
				.then((response) => {
					const responseData = response.data || {};
					const newData = responseData.data || [];
					const total = responseData.total || 0;

					// T11, Log #288: the badge's number. The endpoint filters the rows it will not
					// show INSIDE the paginated query, so this total and this list are the same set.
					this.total = total;

					// Deduplication
					const existingIds = new Set(this.notifications.map(n => n.id));
					const newNotifications = newData.filter(n => !existingIds.has(n.id));

					if (this.page === 1) {
						this.notifications = newNotifications;
					} else {
						this.notifications.push(...newNotifications);
					}

					if (this.notifications.length >= total) {
						this.finished = true;
					}

					this.page++;
				})
				.catch((error) => {
					console.log('Failed to fetch notifications:', error);
				})
				.finally(() => {
					this.loading = false;
				});
		},
		startPolling() {
			this.polling = setInterval(() => {
				this.refreshNotifications();
			}, 10 * 60 * 1000);
		},
		stopPolling() {
			if (this.polling) clearInterval(this.polling);
		},
		refreshNotifications() {
			// Reset and re-fetch latest
			this.page = 1;
			this.notifications = [];
			this.total = 0;
			this.finished = false;
			this.fetchNotifications();
		},
		dismiss(id) {
			axios
				.post(`/api/assetflow/notifications/files/${id}/dismiss`)
				.then(() => {
					this.notifications = this.notifications.filter((n) => n.id !== id);
					// A dismissed job is no longer waiting, so the count loses it too: the badge
					// is the number waiting (Log #288) and the next fetch would say so anyway.
					this.total = Math.max(0, this.total - 1);
				})
				.catch((err) => {
					console.error('Dismiss failed:', err);
				});
		},
		/**
		 * The row's icon. T11 / Log #289: an import that was uploaded and never run takes the
		 * NEUTRAL icon, not the amber "in progress" one -- this component's own default branch,
		 * which is why no class is added here for it.
		 */
		statusIcon(notification) {
			if (notification && notification.not_started) {
				return 'bx bx-info-circle';
			}

			switch (notification && notification.status) {
				case 'done':
				case 'completed':
					return 'bx bx-check-circle';
				case 'processing':
				case 'pending':
					return 'bx bx-loader-circle';
				case 'failed':
				case 'error':
					return 'bx bx-error-circle';
				default:
					return 'bx bx-info-circle';
			}
		},
		/** The same rule for the disc behind the icon: neutral, not amber (Log #289). */
		statusColor(notification) {
			if (notification && notification.not_started) {
				return 'bg-info';
			}

			switch (notification && notification.status) {
				case 'done':
				case 'completed':
					return 'bg-success';
				case 'processing':
				case 'pending':
					return 'bg-warning';
				case 'failed':
				case 'error':
					return 'bg-danger';
				default:
					return 'bg-info';
			}
		}
	},
};
</script>
